/**
 * Toasts, wrapped around sonner so timing and announcements stay consistent.
 * Import `toast` from here, never from 'sonner'.
 *
 * success and info last 5 s; an error stays until dismissed. Every toast is
 * also spoken through the single announcer (assertive for errors); sonner's
 * own live region is switched off in the Toaster so nothing is read twice.
 */
import { toast as sonner } from 'sonner';
import { announce } from '@/lib/announce';

const TOAST_DURATION = {
  success: 5000,
  copy: 2000,
  info: 5000,
} as const;

type ToastId = string | number;

interface ToastOptions {
  description?: string;
  /** One button on the toast, such as "Open". */
  action?: { label: string; onClick: () => void };
  id?: ToastId;
}

export const toast = {
  success(message: string, options: ToastOptions = {}): ToastId {
    announce(message, 'polite');
    return sonner.success(message, {
      duration: options.action
        ? Number.POSITIVE_INFINITY
        : TOAST_DURATION.success,
      closeButton: Boolean(options.action),
      ...options,
    });
  },

  info(message: string, options: ToastOptions = {}): ToastId {
    announce(message, 'polite');
    return sonner.info(message, {
      duration: options.action ? Number.POSITIVE_INFINITY : TOAST_DURATION.info,
      closeButton: Boolean(options.action),
      ...options,
    });
  },

  /** Persistent until dismissed; offers Retry when `onRetry` is given. */
  error(
    message: string,
    options: ToastOptions & { onRetry?: () => void } = {},
  ): ToastId {
    announce(
      options.description ? `${message} ${options.description}` : message,
      'assertive',
    );
    const { onRetry, action, ...rest } = options;
    return sonner.error(message, {
      ...rest,
      duration: Number.POSITIVE_INFINITY,
      closeButton: true,
      action: onRetry ? { label: 'Retry', onClick: onRetry } : action,
    });
  },

  /** "Copied SAOO11B…60BE4". */
  copy(label: string): ToastId {
    const message = `Copied ${label}`;
    announce(message, 'polite');
    return sonner(message, { duration: TOAST_DURATION.copy });
  },

  dismiss(id?: ToastId): void {
    sonner.dismiss(id);
  },
};
